import { useCallback } from 'react';

import { useAudioTrigger } from '@pp/core/src/controllers/audioController/hooks';
import { Logger } from '@pp/core/src/logger/Logger';
import { betActions } from '@pp/core/src/store/slices/bets/slice';
import type { BetType } from '@pp/core/src/store/slices/bets/types';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { popupActions } from '@pp/core/src/store/slices/popup/slice';

import { Bets } from '../../../config/bets';
import { GameCommunicationBetSender } from '../../../controllers/gameCommunication/senders';
import { GameStore, useGameDispatch } from '../../../store/GameStore';
import { crashAutoplayActions } from '../../../store/slices/crashAutoplay/slice';
import type { GetAutoPlayBetType } from '../../../store/slices/crashAutoplay/types';
import { useHighflyerStateSlice } from '../../../store/slices/highflyerState/selectors';
import { highflyerStateActions } from '../../../store/slices/highflyerState/slice';
import { CrashPopupType } from '../../popup/CrashAutoplayModifiedConnected';

const sender = new GameCommunicationBetSender();

export const useOnBetCallbackConnected = (): ((bet: number, betAmount: number) => void) => {
  const dispatch = useGameDispatch();
  const balance = useGameStateSlice.getDeductibleBalance();
  const decimals = useGameStateSlice.getCurrencyInfo()?.digitsNumber || 2;
  const isBetsOpen = useGameStateSlice.isBetsOpenStatus();
  const isFullCashed = useHighflyerStateSlice.isFullCashed();
  const audioTrigger = useAudioTrigger();
  const currentGameId = useGameStateSlice.gameId();
  function checkAutoplay(bet: Bets, betAmount: number): void {
    const { crashAutoplay, autoCashout } = GameStore.getState();
    let autoplayBets;
    if (crashAutoplay.isInProgress[bet]) {
      autoplayBets = crashAutoplay.savedBet[bet] as GetAutoPlayBetType;
    }
    if (autoplayBets) {
      const curentAutoCashout = { isEnable: autoCashout[bet].isEnabled, value: autoCashout[bet].cashoutMultiplier };
      const prevBet: BetType = { betSpots: { [bet]: { value: betAmount } } };
      const singleBetT = {
        betType: prevBet,
        isEnable: curentAutoCashout.isEnable,
        value: curentAutoCashout.value,
      };

      if (
        crashAutoplay.roundsLeft[bet] > 1 &&
        !areBetsAndAuotCahoutEqual(singleBetT, autoplayBets, bet) &&
        !crashAutoplay.isThisRoundOnly[bet]
      ) {
        GameStore.dispatch(
          popupActions.pushPopup(
            bet == 1 ? CrashPopupType.CrashAutoplayModified_1 : CrashPopupType.CrashAutoplayModified_2
          )
        );
      } else {
        let setTRO = false;
        if (crashAutoplay.roundsLeft[bet] == 1) {
          setTRO = true;
        }
        if (!crashAutoplay.isThisRoundOnly[bet] && crashAutoplay.savedBet.gameId !== currentGameId) {
          GameStore.dispatch(crashAutoplayActions.setIsThisRoundOnly({ bestSpot: Number(bet), thisRoundonly: setTRO }));
        }
      }
    }
  }
  return useCallback(
    (betCode: Bets, betAmount: number): void => {
      if (betAmount === 0) return;
      Logger.logEvent('click', 'bet', Date.now(), 1, `${betCode}`, false);
      audioTrigger.play({ name: 'chipClick' });
      if (isBetsOpen) {
        dispatch(
          betActions.pushBet({
            bet: { betSpots: { [betCode]: { value: betAmount } } },
            balance,
            decimals,
            freeChipValue: 0,
            freeChipBalance: 0,
          })
        );
        sender.dispatch('');
      } else {
        dispatch(crashAutoplayActions.setIsCancelled({ bestSpot: betCode, cancelled: false }));
        if (isFullCashed) {
          dispatch(
            betActions.pushBet({
              bet: { betSpots: { [betCode]: { value: betAmount } } },
              balance,
              decimals,
              freeChipValue: 0,
              freeChipBalance: 0,
            })
          );
        }
        dispatch(highflyerStateActions.setEarlyBetAmount({ betCode: betCode, amount: betAmount }));
      }
      dispatch(highflyerStateActions.setIsBetPlaced(betCode));
      checkAutoplay(betCode, betAmount);
    },
    [audioTrigger, isBetsOpen, checkAutoplay, dispatch, balance, decimals, isFullCashed]
  );
};

const areBetsAndAuotCahoutEqual = (bet1: GetAutoPlayBetType, bet2: GetAutoPlayBetType, betId: Bets): boolean => {
  if (!bet1 || !bet2) {
    return false;
  }
  let result = true;
  let betValueChanged = false;
  let autoCashoutValueChanged = false;
  let noChangesToShow = false;
  if (bet1.betType?.betSpots[betId].value !== bet2.betType?.betSpots[betId].value) {
    result = false;
    betValueChanged = true;
  }

  if (bet1.isEnable && (bet1.isEnable !== bet2.isEnable || bet1.value !== bet2.value)) {
    result = false;
    autoCashoutValueChanged = true;
  }
  if (!bet1.isEnable && bet1.isEnable !== bet2.isEnable) {
    result = false;
    autoCashoutValueChanged = false;
    noChangesToShow = true;
  }

  GameStore.dispatch(
    crashAutoplayActions.setAutoplayEnabledChanged({
      bestSpot: betId,
      autoPlayModified: {
        isBetChanged: betValueChanged,
        isAutoCashoutChanged: autoCashoutValueChanged,
        isNoChangesToShow: noChangesToShow,
      },
    })
  );

  return result;
};
